/**
 * Native (Capacitor) platform helpers.
 *
 * Every native-only behaviour in the app MUST be guarded by `isNativeApp()` so
 * the browser/deployed web build behaves exactly as before. These helpers are
 * intentionally synchronous and dependency-light: `@capacitor/core` is safe to
 * import in the web bundle (it simply reports platform "web").
 */
import { Capacitor } from '@capacitor/core';

export type NativePlatform = 'ios' | 'android' | 'web';

/** True only when running inside the Capacitor iOS/Android shell. */
export const isNativeApp = (): boolean => {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

/** Current platform: 'ios' | 'android' | 'web'. */
export const getPlatform = (): NativePlatform => {
  try {
    const p = Capacitor.getPlatform();
    if (p === 'ios' || p === 'android') return p;
    return 'web';
  } catch {
    return 'web';
  }
};

export const isIOSNative = (): boolean => getPlatform() === 'ios';
export const isAndroidNative = (): boolean => getPlatform() === 'android';

/**
 * True when the app is running in any app-like shell — native OR an installed
 * PWA. Use this for "hide the install prompt" style checks.
 */
export const isAppShell = (): boolean => {
  if (isNativeApp()) return true;
  try {
    if ((window.navigator as unknown as { standalone?: boolean }).standalone === true) return true;
    return window.matchMedia('(display-mode: standalone)').matches;
  } catch {
    return false;
  }
};

/** The route the native app should always open on. */
export const NATIVE_HOME_ROUTE = '/admin';
